import React from 'react';
import { ChildrenCapsule, Inner, Label, Root } from './styles';
import { Anchor, LineStyle, VerticalSpaceProps } from './types';

export interface SeparatorProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VerticalSpaceProps {
  leftLabel?: React.ReactNode;
  centerLabel?: React.ReactNode;
  rightLabel?: React.ReactNode;

  lineStyle?: LineStyle;
  lineColor?: string;
  thickness?: number;

  lineWidth?: string;
  align?: Anchor;

  containerBackground?: string;

  leftLabelColor?: string;
  centerLabelColor?: string;
  rightLabelColor?: string;

  labelFontSize?: string;
  labelWeight?: number | string;
  labelPaddingX?: string;

  /**
   * Mostra cápsula com borda ao redor dos children.
   * - Se omitido, será true quando houver children (comportamento anterior).
   * - Se false, remove a borda mas mantém a cápsula (com fundo/padding).
   */
  childrenBorder?: boolean;

  /**
   * Estilo da borda da cápsula dos children.
   * - Quando for 'none', exibe **apenas** o children (sem cápsula, sem fundo, sem padding).
   * - Caso contrário, usa a cápsula normalmente (bordas conforme style + fundo).
   * Padrão: igual a lineStyle.
   */
  childrenBorderStyle?: LineStyle;

  /** Cor da borda da cápsula dos children. Padrão: igual a lineColor. */
  childrenBorderColor?: string;

  /** Raio da borda do contorno dos children. Padrão: '9999px' (pill). */
  radius?: string;

  /** Padding interno da cápsula dos children. Padrão: '0.25rem 0.5rem'. */
  childrenPadding?: string;

  /** Posição da cápsula/children. Padrão: 'center'. */
  childrenPosition?: Anchor;

  'aria-label'?: string;
}

export const SeparatorNovo: React.FC<SeparatorProps> = ({
  leftLabel,
  centerLabel,
  rightLabel,
  lineStyle = 'solid',
  lineColor = '#d9d5d5',
  thickness = 1,
  lineWidth = '100%',
  align = 'center',
  containerBackground = '#ffffff',
  leftLabelColor = '#6b7280',
  centerLabelColor = '#6b7280',
  rightLabelColor = '#6b7280',
  labelFontSize = '0.875rem',
  labelWeight = 500,
  labelPaddingX = '0.5rem',
  children,
  childrenBorder,
  childrenBorderStyle,
  childrenBorderColor,
  radius = '9999px',
  childrenPadding = '0.25rem 0.5rem',
  childrenPosition = 'center',
  margin = 0,
  marginTop = 0,
  marginBottom = 0,
  className,
  ...divProps
}) => {
  const showChildrenBorder =
    typeof childrenBorder === 'boolean' ? childrenBorder : Boolean(children);
  const finalChildrenBorderStyle = childrenBorderStyle ?? lineStyle;
  const finalChildrenBorderColor = childrenBorderColor ?? lineColor;

  return (
    <Root
      role="separator"
      aria-orientation="horizontal"
      className={className}
      margin={margin}
      marginTop={marginTop}
      marginBottom={marginBottom}
      {...divProps}
    >
      <Inner
        lineStyle={lineStyle}
        lineColor={lineColor}
        thickness={thickness}
        lineWidth={lineWidth}
        align={align}
      >
        {leftLabel && (
          <Label
            position="left"
            bg={containerBackground}
            color={leftLabelColor}
            fontSize={labelFontSize}
            fontWeight={labelWeight}
            paddingX={labelPaddingX}
          >
            {leftLabel}
          </Label>
        )}

        {centerLabel && (
          <Label
            position="center"
            bg={containerBackground}
            color={centerLabelColor}
            fontSize={labelFontSize}
            fontWeight={labelWeight}
            paddingX={labelPaddingX}
          >
            {centerLabel}
          </Label>
        )}

        {rightLabel && (
          <Label
            position="right"
            bg={containerBackground}
            color={rightLabelColor}
            fontSize={labelFontSize}
            fontWeight={labelWeight}
            paddingX={labelPaddingX}
          >
            {rightLabel}
          </Label>
        )}

        {children && (
          <ChildrenCapsule
            position={childrenPosition}
            /**
             * Quando style === 'none': exibe somente o children (sem cápsula).
             * Ignora fundo, padding, raio e borda.
             */
            raw={finalChildrenBorderStyle === 'none'}
            bg={containerBackground}
            radius={radius}
            padding={childrenPadding}
            hasBorder={showChildrenBorder}
            borderStyle={finalChildrenBorderStyle}
            borderColor={finalChildrenBorderColor}
          >
            {children}
          </ChildrenCapsule>
        )}
      </Inner>
    </Root>
  );
};
